// Parse the exam-school credentials out of a TESTIFY link (the QR payload) or a
// pasted string, so the teacher never types a bearer token on a phone keyboard.
//
// The real format, confirmed against a live exam-school link (2026-07-26):
//   https://testify.sdobukidnon.org/exam/G7T1-DEMO?token=ab39214f7c9f5ff34165...
// → exam code is the path segment after /exam/, token is the `token` query param.

export interface TestifyCredentials {
  examCode: string;
  /** empty when the payload carried only an exam code (or only a token). */
  token: string;
}

/** A token looks like a long hex string; used to recognise a bare-token paste. */
const TOKEN_RE = /^[A-Fa-f0-9]{24,}$/;

/**
 * Pull { examCode, token } out of `text`. Accepts, in order:
 *   1. a TESTIFY exam URL (the QR payload) — exam code from /exam/<code>, token from ?token=
 *   2. a bare token (long hex string)
 * Returns null when neither is recognisable. A partial result (one field filled)
 * is still returned — the caller keeps the other field for manual entry.
 */
export function parseTestifyLink(text: string): TestifyCredentials | null {
  const raw = (text ?? '').trim();
  if (!raw) return null;

  const url = asUrl(raw);
  if (url) {
    // ?token= is the observed carrier; accept a couple of obvious aliases so a
    // slightly different QR from TESTIFY still works instead of hard-failing.
    const token = firstParam(url, ['token', 'exam_school_token', 'access_token']) ?? '';
    const examCode = examCodeFromPath(url) || firstParam(url, ['exam_code', 'code', 'exam']) || '';
    if (!token && !examCode) return null;
    return { examCode, token };
  }

  if (TOKEN_RE.test(raw)) return { examCode: '', token: raw };
  return null;
}

function asUrl(raw: string): URL | null {
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) return null;
  try {
    return new URL(raw);
  } catch {
    return null;
  }
}

function firstParam(url: URL, names: string[]): string | null {
  for (const n of names) {
    const v = url.searchParams.get(n);
    if (v && v.trim()) return v.trim();
  }
  return null;
}

/** /exam/G7T1-DEMO → "G7T1-DEMO"; falls back to the last path segment. */
function examCodeFromPath(url: URL): string {
  const parts = url.pathname.split('/').filter(Boolean).map(decodeURIComponent);
  if (!parts.length) return '';
  const afterExam = parts.indexOf('exam');
  if (afterExam >= 0 && parts[afterExam + 1]) return parts[afterExam + 1];
  return parts[parts.length - 1];
}
